import { Component, OnInit } from '@angular/core';
import { Validators, FormGroup, FormControl, AbstractControl, ValidationErrors } from '@angular/forms';
import { WebService}  from '../../services/web/web.service';
import { ToastService } from '../../services/notification/toast.service';
import {SpinnerService} from '../../services/spinner/spinner.service';
import { Router } from '@angular/router';
import {SecurityService} from "../../services/security.service";
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-preferences',
    templateUrl: './preferences.component.html',
    styleUrls: ['./preferences.component.scss'],
    standalone: false
})
export class PreferencesComponent implements OnInit {
  form: FormGroup;
  public username: string = '';
  
  // Visibility toggles for each password input
  showActualPassword: boolean = false;
  showNewPassword: boolean = false;
  showNewPasswordConfirm: boolean = false;

  constructor(private securityService : SecurityService,
              private spinner: SpinnerService,
              private webService: WebService,
              private toast: ToastService,
              private router : Router,
              private translate: TranslateService) {
    this.form = this.createFormGroup();
  }

  ngOnInit(): void {
    this.username = this.securityService.getTokenSub() || '';
  }

  createFormGroup(): FormGroup {
    const newPassword = new FormControl('', [
      Validators.required,
      this.passwordValidator
    ]);
    const newPasswordConfirm = new FormControl('', [Validators.required, this.passwordsMatchValidator(newPassword)]);

    return new FormGroup({
      actual_password: new FormControl('', [Validators.required]),
      new_password: newPassword,
      newPasswordConfirm: newPasswordConfirm
    });
  }

  /**
   * Validador personalizado para contraseñas seguras
   * Requisitos:
   * - Mínimo 12 caracteres
   * - Al menos 1 mayúscula (A-Z)
   * - Al menos 1 minúscula (a-z)
   * - Al menos 1 número (0-9)
   * - Al menos 1 carácter especial (!@#$%^&*()-_=+[]{};:,.<>?/|)
   */
  passwordValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;

    if (!value) {
      return null;
    }

    const hasMinLength = value.length >= 12;
    const hasUpperCase = /[A-Z]/.test(value);
    const hasLowerCase = /[a-z]/.test(value);
    const hasNumber = /[0-9]/.test(value);
    const hasSpecialChar = /[!@#$%^&*()\\-_=+\\\[\\\]{};:,.<>?/|\\\\'""]/.test(value);

    const passwordValid = hasMinLength && hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar;

    if (!passwordValid) {
      return {
        passwordStrength: {
          hasMinLength,
          hasUpperCase,
          hasLowerCase,
          hasNumber,
          hasSpecialChar
        }
      };
    }

    return null;
  }


  // Función personalizada de validación
  passwordsMatchValidator(passwordField: AbstractControl): (control: AbstractControl) => ValidationErrors | null {
    return (control: AbstractControl): ValidationErrors | null => {
      return control.value === passwordField.value ? null : { 'mismatch': true };
    };
  }

  send(){
    this.form.value.user = this.securityService.getTokenAdminOrUser();

    if (!this.form.valid){
      return this.toast.showError(this.translate.instant('preferences.formIncomplete'));
    }

    this.spinner.open();

    this.webService.put(this.form.value, this.webService.HOST + "/onboarding/changePassword").subscribe( response =>{
      this.spinner.close();
      this.form.reset();
      this.toast.showSuccess(this.translate.instant('preferences.passwordChanged'));
    }, err =>{
      this.spinner.close();
      this.toast.showError(err);
      console.log(err)
    })
  }

  goBack(){
    this.router.navigate(["/my-portals"]); 
  }

  // Métodos para alternar visibilidad
  toggleActualPassword() {
    this.showActualPassword = !this.showActualPassword;
  }

  toggleNewPassword() {
    this.showNewPassword = !this.showNewPassword;
  }

  toggleNewPasswordConfirm() {
    this.showNewPasswordConfirm = !this.showNewPasswordConfirm;
  }

  /**
   * Genera una contraseña aleatoria que cumple con todos los requisitos de seguridad
   */
  generatePassword(): void {
    const uppercaseChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowercaseChars = 'abcdefghijklmnopqrstuvwxyz';
    const numberChars = '0123456789';
    const specialChars = '!@#$%^&*()-_=+[]{};:,.<>?/|';

    // Asegurar que tenga al menos uno de cada tipo requerido
    let password = '';
    password += uppercaseChars[Math.floor(Math.random() * uppercaseChars.length)];
    password += lowercaseChars[Math.floor(Math.random() * lowercaseChars.length)];
    password += numberChars[Math.floor(Math.random() * numberChars.length)];
    password += specialChars[Math.floor(Math.random() * specialChars.length)];

    // Completar hasta 12 caracteres con caracteres aleatorios de todos los tipos
    const allChars = uppercaseChars + lowercaseChars + numberChars + specialChars;
    for (let i = password.length; i < 12; i++) {
      password += allChars[Math.floor(Math.random() * allChars.length)];
    }

    // Mezclar los caracteres para que no sigan un patrón predecible
    password = password.split('').sort(() => Math.random() - 0.5).join('');

    // Establecer la contraseña en el formulario
    this.form.patchValue({
      new_password: password,
      newPasswordConfirm: password
    });
    this.toast.showSuccess('Contraseña generada exitosamente');
  }

  /**
   * Obtiene los errores de validación del password para mostrar en el template
   */
  getPasswordErrors(): any {
    const passwordControl = this.form.get('new_password');
    if (passwordControl?.hasError('passwordStrength')) {
      return passwordControl.errors?.['passwordStrength'];
    }
    return null;
  }
}
